import Vue from 'vue';
import {indexOf, map} from 'lodash';
import {
    createFloorPlan,
    loadFloorPlans,
    removeFloorPlan,
    updateFloorPlan,
    loadFloors,
    createFloor,
    updateFloor,
    removeFloor,
    createRoom,
    updateRoom,
    removeRoom
} from '../modules/api/floor-plan.api';

function initialState() {
    return {
        floorPlanId: null,
        floorPlans:  [],
        floors:      [],
    }
}

const getters = {
    floorPlanId:               (state) => state.floorPlanId,
    floorPlans:                (state) => state.floorPlans,
    floorPlansForAutocomplete: (state) => state.floorPlans.map(({ id, title }) => {
        return { name: title, id };
    }),
    loadFloorPlanById:         (state, getters) => (floorPlanId) => {
        return new Promise((resolve) => resolve(getters.floorPlans.find((floorPlan) => floorPlan.id === floorPlanId) || null));
    },

    floors:           (state) => state.floors,
    loadFloorById:    (state, getters) => (floorId) => {
        return new Promise((resolve) => resolve(getters.floors.find((floor) => floor.id === floorId) || null));
    },
    currentFloorPlan: (state, getters) => {
        const floorPlanId = getters.floorPlanId;
        const floorPlan = getters.floorPlans.find(({ id }) => floorPlanId === id);
        return floorPlan || null;
    }
}

const actions = {
    async createFloorPlan({ commit, rootGetters }, data) {
        const projectId = rootGetters['projects/project/projectId'];
        return createFloorPlan({ ...data, projectId }).then((data) => commit('ADD_FLOOR_PLAN', data))
    },
    async updateFloorPlan({ commit }, data) {
        return updateFloorPlan(data).then((data) => commit('UPDATE_FLOOR_PLAN', data))
    },
    async removeFloorPlan({ commit }, floorPlanId) {
        return removeFloorPlan(floorPlanId).then((id) => commit('REMOVE_FLOOR_PLAN', id))
    },
    async loadFloorPlans({ commit, rootGetters }) {
        const projectId = rootGetters['projects/project/projectId'];
        return loadFloorPlans(projectId).then((data) => commit('SET_FLOOR_PLANS', data));
    },

    // FLOORS
    setFloorPlanId: ({ commit, dispatch }, floorPlanId) => commit('SET_FLOOR_PLAN_ID', floorPlanId),
    initFloors:     ({ dispatch }, floorPlanId) => {
        dispatch('resetFloors');
        dispatch('setFloorPlanId', floorPlanId);
        dispatch('loadFloors');
    },
    async updateFloor({ commit }, data) {
        return updateFloor(data).then((data) => commit('UPDATE_FLOOR', data))
    },
    async removeFloor({ commit }, floorId) {
        return removeFloor(floorId).then((id) => commit('REMOVE_FLOOR', id))
    },
    async loadFloors({ commit, getters }) {
        const floorPlanId = getters.floorPlanId;
        return loadFloors(floorPlanId).then((data) => commit('SET_FLOORS', data));
    },
    async createFloor({ commit, getters }, data) {
        const floorPlanId = getters.floorPlanId;
        return createFloor({ ...data, floorPlanId }).then((data) => commit('ADD_FLOOR', data))
    },
    resetFloors:    ({ commit }) => commit('SET_FLOORS', []),

    // ROOMS
    async createRoom({ commit }, data) {
        const { floorId } = data;
        return createRoom(data).then((data) => commit('ADD_FLOOR_ROOM', { ...data, floorId }))
    },
    async updateRoom({ commit }, data) {
        const { floorId } = data;
        return updateRoom(data).then((data) => commit('UPDATE_FLOOR_ROOM', { ...data, floorId }))
    },
    async removeRoom({ commit }, data) {
        const { id } = data;
        return removeRoom(id).then(() => commit('REMOVE_FLOOR_ROOM', data))
    },

    reset: ({ commit }) => commit('RESET'),
}

const mutations = {
    SET_FLOOR_PLANS:   (state, floorPlansData) => state.floorPlans = floorPlansData,
    ADD_FLOOR_PLAN:    (state, floorPlanData) => state.floorPlans.push(floorPlanData),
    UPDATE_FLOOR_PLAN: (state, floorPlanData) => {
        const index = indexOf(map(state.floorPlans, (object) => object.id), floorPlanData.id);
        Vue.set(state.floorPlans, index, floorPlanData);
    },
    REMOVE_FLOOR_PLAN: (state, floorPlanId) => {
        const index = indexOf(map(state.floorPlans, (object) => object.id), floorPlanId);
        if (index !== -1) {
            state.floorPlans.splice(index, 1);
        }
    },

    // FLORS
    SET_FLOOR_PLAN_ID: (state, floorPlanId) => state.floorPlanId = floorPlanId,
    SET_FLOORS:        (state, floors) => state.floors = floors,
    ADD_FLOOR:         (state, floor) => state.floors.push(floor),
    UPDATE_FLOOR:      (state, floorData) => {
        const index = indexOf(map(state.floors, (object) => object.id), floorData.id);
        Vue.set(state.floors, index, floorData);
    },
    REMOVE_FLOOR:      (state, floorId) => {
        const index = indexOf(map(state.floors, (object) => object.id), floorId);
        if (index !== -1) {
            state.floors.splice(index, 1);
        }
    },

    // ROOMS
    ADD_FLOOR_ROOM:    (state, data) => {
        const { floorId } = data;
        const index = indexOf(map(state.floors, (object) => object.id), floorId);
        if (index !== -1) {
            const floor = state.floors[index];
            delete data.floorId;
            floor.rooms.push(data);
            Vue.set(state.floors, index, floor);
        }
    },
    UPDATE_FLOOR_ROOM: (state, data) => {
        const { floorId, id } = data;
        const index = indexOf(map(state.floors, (object) => object.id), floorId);
        if (index !== -1) {
            const floor = state.floors[index];
            const roomIndex = indexOf(map(floor.rooms, (object) => object.id), id);
            if (roomIndex !== -1) {
                delete data.floorId;
                Vue.set(state.floors[index].rooms, roomIndex, data);
            }
        }
    },
    REMOVE_FLOOR_ROOM: (state, ids) => {
        const { id, floorId } = ids;
        const index = indexOf(map(state.floors, (object) => object.id), floorId);
        if (index !== -1) {
            const floor = state.floors[index];
            const roomIndex = indexOf(map(floor.rooms, (object) => object.id), id);
            if (roomIndex !== -1) {
                floor.rooms.splice(roomIndex, 1);
            }
        }
    },

    RESET: (state) => {
        const s = initialState();
        Object.keys(s).forEach(key => state[key] = s[key]);
    }
}

export default {
    namespaced: true,
    state:      initialState,
    getters,
    actions,
    mutations
}
